import React, { useState, useMemo, createElement } from "react";
import { View, StyleSheet, Platform, ScrollView } from "react-native";
import CustomText from "../common/general/CustomText/CustomText";
import CustomButton from "../common/general/CustomButton/CustomButton";
import CustomModal from "../common/general/CustomModal/CustomModal";
import CustomSpacing from "../common/layout/CustomSpacing/CustomSpacing";
import { AlignLeftIcon, EyeIcon, InfoIcon, WarningIcon } from "@phosphor-icons/react";
import { sanitizeHtmlByDescriptionFormat, getSanitizationRemovals } from "../../utils/sanitizeHtml";

interface HtmlSourceEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  iframeDomainAllowlist?: string[];
}

const VOID_ELEMENTS = /^(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)$/i;

/** Indents HTML source for readability. Does not modify content. */
function formatHtml(html: string): string {
  const TAB = "  ";
  let indent = 0;
  let result = "";

  const normalized = html.replace(/>\s+</g, "><").trim();
  const tokenRegex = /(<[^>]+>)|([^<]+)/g;
  let match: RegExpExecArray | null;

  while ((match = tokenRegex.exec(normalized)) !== null) {
    const [, tag, text] = match;
    if (text) {
      if (text.trim()) result += TAB.repeat(indent) + text.trim() + "\n";
    } else if (tag) {
      const isClose = tag.startsWith("</");
      const isSelfClose = /\/>$/.test(tag) || VOID_ELEMENTS.test(tag.replace(/^<([a-z]+).*/i, "$1"));
      const isNoIndent = tag.startsWith("<!--") || tag.startsWith("<!");
      if (isClose) {
        indent = Math.max(0, indent - 1);
        result += TAB.repeat(indent) + tag + "\n";
      } else {
        result += TAB.repeat(indent) + tag + "\n";
        if (!isSelfClose && !isNoIndent) indent++;
      }
    }
  }

  return result.trim();
}

/** Web-only: renders sanitized HTML (including iframes) via the DOM so embeds show in preview. */
function PreviewHtml({ html }: { html: string }) {
  return createElement("div", {
    "data-testid": "html-source-preview",
    dangerouslySetInnerHTML: { __html: html },
    style: previewRootStyle,
  });
}

const previewRootStyle = {
  padding: 20,
  minHeight: 120,
  width: "100%" as const,
  boxSizing: "border-box" as const,
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: 16,
  lineHeight: 1.6,
  color: "#1f2937",
};

const HtmlSourceEditor: React.FC<HtmlSourceEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter HTML here…",
  disabled = false,
  iframeDomainAllowlist,
}) => {
  const [showCompatibilityModal, setShowCompatibilityModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const sanitizedForPreview = useMemo(
    () => (value ? sanitizeHtmlByDescriptionFormat("HTML", value, iframeDomainAllowlist) : ""),
    [value, iframeDomainAllowlist]
  );

  const removals = useMemo(
    () => (value ? getSanitizationRemovals(value, iframeDomainAllowlist) : []),
    [value, iframeDomainAllowlist]
  );

  const handlePreviewClick = () => {
    if (!value?.trim()) {
      setShowPreviewModal(true);
      return;
    }
    if (removals.length > 0) {
      setShowCompatibilityModal(true);
      return;
    }
    setShowPreviewModal(true);
  };

  const handleContinueToPreview = () => {
    setShowCompatibilityModal(false);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  if (Platform.OS !== "web") {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.infoBanner}>
        <InfoIcon size={18} color="#0369a1" style={styles.infoIcon} />
        <CustomText
          text="Paste or type HTML below. When you save, we sanitize for security: scripts are removed and only allowed embed domains (e.g. YouTube, CodePen) are kept. Use Preview to see how it will look."
          size="xs"
          useTranslationText={false}
          style={styles.infoText}
        />
      </View>

      <View style={styles.toolbar}>
        <CustomText text="HTML Source" size="s" weight="medium" useTranslationText={false} style={styles.label} />
        <View style={styles.toolbarActions}>
          <CustomButton
            onPress={() => value?.trim() && onChange(formatHtml(value))}
            styleType="lightTransparent"
            size="s"
            leftIcon={<AlignLeftIcon size={18} color="#6b7280" />}
            text="Format"
            textStyle={styles.formatButtonText}
          />
          <CustomButton
            onPress={handlePreviewClick}
            styleType="lightTransparent"
            size="s"
            leftIcon={<EyeIcon size={18} color="#0f766e" weight="duotone" />}
            text="Preview"
            textStyle={styles.previewButtonText}
          />
        </View>
      </View>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
        style={textareaStyle}
      />

      {/* Step 1: Compatibility — what will be removed; show before preview when there are removals */}
      <CustomModal
        visible={showCompatibilityModal}
        onRequestClose={() => setShowCompatibilityModal(false)}
        title="Content that will be removed"
        showCloseButton
        translateTitle={false}
        style={styles.compatModal}>
        <View style={styles.compatContent}>
          <View style={styles.compatIconRow}>
            <WarningIcon size={24} color="#b45309" weight="fill" />
            <CustomText
              text="The following won't appear in the preview or when saved (removed for security):"
              size="s"
              useTranslationText={false}
              style={styles.compatMessage}
            />
          </View>
          <View style={styles.removalList}>
            {removals.map((r, i) =>
              r.type === "script" ? (
                <View key={i} style={styles.removalItem}>
                  <CustomText
                    text={`${r.count} script tag${r.count > 1 ? "s" : ""} (scripts are not allowed)`}
                    size="s"
                    useTranslationText={false}
                    style={styles.removalText}
                  />
                </View>
              ) : (
                <View key={i} style={styles.removalItem}>
                  <CustomText
                    text={`Iframe from "${r.hostname}" (domain not in allowlist)`}
                    size="s"
                    useTranslationText={false}
                    style={styles.removalText}
                  />
                </View>
              )
            )}
          </View>
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Do you want to continue and see the preview of the remaining content?"
            size="s"
            useTranslationText={false}
            style={styles.compatSubtext}
          />
          <CustomSpacing type="vertical" size="l" />
          <View style={styles.compatButtons}>
            <CustomButton
              text="Cancel"
              styleType="lightTransparent"
              onPress={() => setShowCompatibilityModal(false)}
              accessibilityLabel="Cancel"
            />
            <CustomButton
              text="Continue to preview"
              styleType="primary"
              onPress={handleContinueToPreview}
              accessibilityLabel="Continue to preview"
            />
          </View>
        </View>
      </CustomModal>

      {/* Step 2: Preview popup */}
      <CustomModal
        visible={showPreviewModal}
        onRequestClose={handleClosePreview}
        title="Content preview"
        showCloseButton
        translateTitle={false}
        style={styles.previewModal}
        bodyStyle={styles.previewModalBody}>
        <View style={styles.previewModalInner}>
          <View style={styles.previewBadge}>
            <CustomText
              text="This is how your content will look when saved. Embeds use allowed domains only."
              size="xs"
              useTranslationText={false}
              style={styles.previewBadgeText}
            />
          </View>
          <ScrollView
            style={styles.previewScroll}
            contentContainerStyle={styles.previewScrollContent}
            showsVerticalScrollIndicator={true}>
            {value?.trim() ? (
              <PreviewHtml html={sanitizedForPreview} />
            ) : (
              <CustomText
                text="Nothing to preview yet. Add some HTML above and click Preview again."
                size="s"
                useTranslationText={false}
                style={styles.emptyPreview}
              />
            )}
          </ScrollView>
        </View>
      </CustomModal>
    </View>
  );
};

const textareaStyle = {
  width: "100%" as const,
  minHeight: 220,
  maxHeight: 480,
  padding: 14,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  fontSize: 13,
  lineHeight: 1.5,
  color: "#1f2937",
  backgroundColor: "#f9fafb",
  border: "1px solid #d1d5db",
  borderRadius: 10,
  resize: "vertical" as const,
  outline: "none" as const,
  tabSize: 2,
};

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  infoBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    padding: 12,
    backgroundColor: "#f0f9ff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#bae6fd",
  },
  infoIcon: {
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    color: "#0369a1",
    lineHeight: 19,
  },
  toolbar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  toolbarActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  formatButtonText: {
    fontSize: 14,
    color: "#6b7280",
    fontWeight: "600",
  },
  label: {
    color: "#374151",
  },
  previewButtonText: {
    fontSize: 14,
    color: "#0f766e",
    fontWeight: "600",
  },
  compatModal: {
    maxWidth: 440,
  },
  compatContent: {
    paddingVertical: 8,
  },
  compatIconRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 12,
  },
  compatMessage: {
    flex: 1,
    color: "#374151",
    lineHeight: 20,
  },
  removalList: {
    backgroundColor: "#fef3c7",
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: "#fcd34d",
  },
  removalItem: {
    paddingVertical: 4,
  },
  removalText: {
    color: "#92400e",
    fontFamily: "ui-monospace, monospace",
    fontSize: 13,
  },
  compatSubtext: {
    color: "#6b7280",
    lineHeight: 20,
  },
  compatButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  previewModal: {
    maxWidth: 720,
    maxHeight: "85%",
  },
  previewModalBody: {
    flex: 1,
    minHeight: 320,
    maxHeight: 560,
  },
  previewModalInner: {
    flex: 1,
    minHeight: 280,
  },
  previewBadge: {
    backgroundColor: "#ecfdf5",
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#a7f3d0",
  },
  previewBadgeText: {
    color: "#065f46",
    lineHeight: 18,
  },
  previewScroll: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    backgroundColor: "#ffffff",
  },
  previewScrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  emptyPreview: {
    color: "#9ca3af",
    fontStyle: "italic",
    padding: 20,
  },
});

export default HtmlSourceEditor;
